import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import {animate, style, transition, trigger} from "@angular/animations";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {Subscription} from "rxjs";
import type {User} from "src/app/modules/auth/models/User";
import {ModalComponent} from "../../../core/components/modal/modal.component";
import {ModalService} from "../../../core/services/modal.service";
import {ToastService} from "../../../core/services/toast.service";
import {WorkBulkActionsService} from "../../../shared/services/work-bulk-actions.service";
import type {WorkBulkActionResponse} from "../../../shared/interfaces/work-bulk-action.interface";
import {WorkSelectionContext} from "../../enums/work-selection-context.enum";
import type {
  WorkSelectionActionCompletion,
  WorkSelectionItem,
  WorkSelectionReference,
} from "../../interfaces/work-selection.interface";

@Component({
  selector: "app-work-selection-actions",
  templateUrl: "./work-selection-actions.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger("selectionBar", [
      transition(":enter", [
        style({opacity: 0, transform: "translateY(10px)"}),
        animate(
          "220ms cubic-bezier(0.22, 1, 0.36, 1)",
          style({opacity: 1, transform: "translateY(0)"})
        ),
      ]),
      transition(":leave", [
        animate(
          "170ms cubic-bezier(0.4, 0, 0.2, 1)",
          style({opacity: 0, transform: "translateY(10px)"})
        ),
      ]),
    ]),
  ],
  imports: [TranslateModule, ModalComponent],
})
export class WorkSelectionActionsComponent implements OnInit, OnDestroy {
  @Input() selection: WorkSelectionItem[] = [];
  @Input() context = WorkSelectionContext.Library;
  @Input() user!: User;
  @Input() listId?: string;
  @Input() isMobile = false;
  @Input() canRemove = true;
  @Output() successes = new EventEmitter<WorkSelectionActionCompletion>();
  @Output() clearRequested = new EventEmitter<void>();
  @Output() busyChanged = new EventEmitter<boolean>();

  @Input() actionsModalId = "work_selection_actions";
  @Input() removeModalId = "work_selection_remove";
  busy = false;
  private readonly subscriptions: Subscription[] = [];

  constructor(
    public readonly modalService: ModalService,
    private readonly bulkActionsService: WorkBulkActionsService,
    private readonly toastService: ToastService,
    private readonly translate: TranslateService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.modalService.bulkActionCompleted.subscribe((completion) => {
        if (this.selection.length > 0) {
          this.successes.emit(completion);
        }
      })
    );
  }

  openActions(): void {
    this.modalService.openModal(this.actionsModalId);
  }

  openAddToList(): void {
    const first = this.selection[0]?.work;
    if (!first) {
      return;
    }
    this.closeActionsModal();
    this.modalService.openItemModal(
      `addList_${first.providerId}`,
      first,
      undefined,
      this.selection.map((item) => item.work)
    );
  }

  openRecommendation(): void {
    const first = this.selection[0]?.work;
    if (!first) {
      return;
    }
    this.closeActionsModal();
    this.modalService.openItemModal(
      `send_work_${first._id}`,
      first,
      undefined,
      this.selection.map((item) => item.work)
    );
  }

  openRemoveConfirmation(): void {
    if (!this.canRemove) {
      return;
    }
    this.closeActionsModal();
    this.modalService.openModal(this.removeModalId);
  }

  removeSelection(): void {
    if (!this.canRemove || this.busy || this.selection.length === 0) {
      return;
    }
    this.setBusy(true);
    this.cdr.markForCheck();

    if (this.context === WorkSelectionContext.Recommendations) {
      this.hideSelectedRecommendations();
      return;
    }

    const references = this.selection.map((item) => item.reference);
    const request = this.getRemoveRequest(references);
    if (!request) {
      this.finishWithError("UNKNOWN_ERROR");
      return;
    }
    this.subscriptions.push(
      request.subscribe({
        next: (response) => this.handleWorkResponse(response),
        error: () => this.finishWithError("UNKNOWN_ERROR"),
      })
    );
  }

  get removeLabel(): string {
    switch (this.context) {
      case WorkSelectionContext.Followed:
        return "WORK_SELECTION_UNFOLLOW";
      case WorkSelectionContext.Recommendations:
        return "WORK_SELECTION_HIDE_RECOMMENDATIONS";
      case WorkSelectionContext.List:
        return "WORK_SELECTION_REMOVE_FROM_LIST";
      default:
        return "WORK_SELECTION_REMOVE_FROM_LIBRARY";
    }
  }

  ngOnDestroy(): void {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
    this.setBusy(false);
  }

  private setBusy(busy: boolean): void {
    this.busy = busy;
    this.busyChanged.emit(busy);
  }

  private getRemoveRequest(references: WorkSelectionReference[]) {
    if (this.context === WorkSelectionContext.Followed) {
      return this.bulkActionsService.unfollow(this.user._id, references);
    }
    if (this.context === WorkSelectionContext.List && this.listId) {
      return this.bulkActionsService.removeFromList(
        this.user._id,
        this.listId,
        references
      );
    }
    return this.bulkActionsService.deleteFromLibrary(this.user._id, references);
  }

  private hideSelectedRecommendations(): void {
    const ids = [
      ...new Set(this.selection.flatMap((item) => item.recommendationIds)),
    ];
    if (ids.length === 0) {
      this.finishWithError("UNKNOWN_ERROR");
      return;
    }
    this.subscriptions.push(
      this.bulkActionsService.hideRecommendations(ids).subscribe({
        next: (response) => {
          const successfulIds = new Set(
            response.results
              .filter((result) => result.status === "success")
              .map((result) => result.recommendationId)
          );
          const references = this.selection
            .filter(
              (item) =>
                item.recommendationIds.length > 0 &&
                item.recommendationIds.every((id) => successfulIds.has(id))
            )
            .map((item) => item.reference);
          this.finish(references, true);
        },
        error: () => this.finishWithError("UNKNOWN_ERROR"),
      })
    );
  }

  private handleWorkResponse(response: WorkBulkActionResponse): void {
    const references = response.results
      .filter((result) => result.status === "success")
      .map((result) => result.work);
    this.finish(references, true);
  }

  private finish(
    references: WorkSelectionReference[],
    removesFromSource: boolean
  ): void {
    this.setBusy(false);
    this.modalService.closeModal(this.removeModalId);
    this.successes.emit({references, removesFromSource});
    const key =
      references.length === this.selection.length
        ? "WORK_SELECTION_ACTION_SUCCESS"
        : "WORK_SELECTION_ACTION_PARTIAL";
    const classes =
      references.length === this.selection.length
        ? "bg-form-success"
        : references.length > 0
        ? "bg-primary"
        : "bg-form-error";
    this.sendToast(key, classes);
    this.cdr.markForCheck();
  }

  private finishWithError(key: string): void {
    this.setBusy(false);
    this.sendToast(key, "bg-form-error");
    this.cdr.markForCheck();
  }

  private sendToast(key: string, classes: string): void {
    this.subscriptions.push(
      this.translate.get(key).subscribe((text) => {
        this.toastService.sendToast({text, translatable: false, classes});
      })
    );
  }

  private closeActionsModal(): void {
    if (this.modalService.isModalOpen(this.actionsModalId)) {
      this.modalService.closeModal(this.actionsModalId);
    }
  }
}
